import { useState, useRef, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import ToggleGroup from '@sdk/components/forms/components/toggleGroup/ToggleGroup';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import TextField from '@sdk/components/forms/TextField';
import Padding from '@sdk/components/ui/spacing/Padding';
import Picker from '@sdk/components/forms/Picker';
import View from '@sdk/components/primitives/View';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ClockInGeoMode } from '@sdk/authentication/types';
import validateNumber, { ValidateOptions } from '@sdk/utils/validation/validateNumber';

import { TabType } from '../../types';
import type { TimeClockGeofencingModesOption } from '../../types';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { getNumberFromBoolean, handleGeofencingModesChange } from '../../helpers/utils';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { FIELD_WIDTH_100, UNIT_SYSTEMS } from '../../constants';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

import getGeofencingOptions from './helpers/getGeofencingOptions';
import {
  MIN_FEET_DISTANCE,
  MIN_METERS_DISTANCE,
  MAX_FEET_DISTANCE,
  MAX_METERS_DISTANCE,
} from './Geofencing.consts';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_100}px;
`;

const StyledRow = styled(Row)`
  align-items: baseline;
  justify-content: space-between;
  flex-shrink: 1;
`;

const Geofencing: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { currentUser } = useAuth();
  const { toggleError, isFieldValid } = useSettingsErrorsContext();
  const { userOrgSettings, onUserOrgSettingsChange, initialUserOrgSettings } = useUserOrgSettings();

  const [gpsEnabled, setGpsEnabled] = useState(userOrgSettings.clockInMaxDistance !== 0);
  const radiusField = useRef(null);
  const previousGpsEnabledValue = useRef(userOrgSettings.clockInMaxDistance !== 0);

  useEffect(() => {
    setGpsEnabled(userOrgSettings.clockInMaxDistance !== 0);

    if (!previousGpsEnabledValue.current && gpsEnabled) {
      radiusField.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrgSettings.clockInMaxDistance]);

  useEffect(() => {
    if (!previousGpsEnabledValue.current && gpsEnabled) {
      radiusField.current.focus();
    }
    previousGpsEnabledValue.current = gpsEnabled;
  }, [gpsEnabled]);

  const onGeofancingModeChange = (option: TimeClockGeofencingModesOption[]): void => {
    const geofencingMode = handleGeofencingModesChange(option);

    if (!geofencingMode) {
      onUserOrgSettingsChange({
        clockInGeoRequired: Boolean(geofencingMode),
        clockInGeoMode: initialUserOrgSettings.clockInGeoMode,
      });
      return;
    }

    onUserOrgSettingsChange({ clockInGeoMode: geofencingMode });
  };

  const onGeofencingRequiredeChange = (value: boolean): void => {
    if (value) {
      onUserOrgSettingsChange({ clockInGeoRequired: value, clockInGeoMode: ClockInGeoMode.both });
      return;
    }

    onUserOrgSettingsChange({
      clockInGeoRequired: value,
      clockInMaxDistance: initialUserOrgSettings.clockInMaxDistance,
    });
    toggleError(TabType.timeClock, 'clockInMaxDistance', false);
  };

  const onClockInMaxDistanceSwitchChange = (value: boolean): void => {
    setGpsEnabled(value);

    if (!value) {
      onUserOrgSettingsChange({ clockInMaxDistance: 0 });
      toggleError(TabType.timeClock, 'clockInMaxDistance', false);
    } else {
      onUserOrgSettingsChange({ clockInMaxDistance: null });
    }
  };

  const validateMaxDistance = (value: number): boolean => {
    return validateNumber(value, {
      [ValidateOptions.integer]: true,
      [ValidateOptions.validNumberGreaterOrEqualThan]: userOrgSettings.imperialUnits
        ? MIN_FEET_DISTANCE
        : MIN_METERS_DISTANCE,
      [ValidateOptions.validNumberLowerOrEqualThan]: userOrgSettings.imperialUnits
        ? MAX_FEET_DISTANCE
        : MAX_METERS_DISTANCE,
    });
  };

  const getErrorMessage = (value: number): string => {
    if (userOrgSettings.imperialUnits) {
      const limitRadius = value > 3300 ? MAX_FEET_DISTANCE : MIN_FEET_DISTANCE;
      return gettext(`Within %(limitRadius)s feet radius of location`, { limitRadius });
    }

    const limitRadius = value > 1000 ? MAX_METERS_DISTANCE : MIN_METERS_DISTANCE;
    return gettext(`Within %(limitRadius)s meters radius of location`, { limitRadius });
  };

  return (
    <CollapsibleSettingsSection
      withSeparator
      isActive={userOrgSettings.clockInGeoRequired}
      headerItem={
        <SettingsSwitcherRow
          disabled={!currentUser.isAdmin}
          pBottom={20}
          value={userOrgSettings.clockInGeoRequired}
          onChange={onGeofencingRequiredeChange}
          subtitle={gettext(
            'Ensure employees are in the right place when they are clocking in and out.'
          )}
        />
      }
      contentItem={
        <>
          <Column pBottom={20}>
            <RowSpaceBetween>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Turn on geofencing feature for:')}
              </Text14>
              <ToggleGroup
                onChange={onGeofancingModeChange}
                options={getGeofencingOptions(userOrgSettings.clockInGeoMode)}
              />
            </RowSpaceBetween>
            <Text14 pTop={20} themeColor={ThemeColorType.gray}>
              {gettext('Allow clocking in and out if either of the following conditions are met:')}
            </Text14>
          </Column>

          <CollapsibleSettingsSection
            isActive={userOrgSettings.clockInGeoRequired && gpsEnabled}
            headerItem={
              <SettingsSwitcherRow
                disabled={!currentUser.isAdmin}
                pTop={20}
                pBottom={20}
                value={gpsEnabled}
                onChange={(value): void => {
                  onClockInMaxDistanceSwitchChange(value);
                }}
                title={gettext("Enable GPS within specified radius from the location's address")}
              />
            }
            contentItem={
              <Column pBottom={20}>
                <StyledRow>
                  <Column>
                    <Text14 themeColor={ThemeColorType.gray}>
                      <TranslateWithComponents
                        replaceComponents={{
                          distance: (
                            <Text14 themeColor={ThemeColorType.primary}>
                              {userOrgSettings.clockInMaxDistance || '0'}
                            </Text14>
                          ),
                          uints: (
                            <Text14 themeColor={ThemeColorType.primary}>
                              {userOrgSettings.imperialUnits ? 'feet' : 'meters'}
                            </Text14>
                          ),
                        }}>
                        {gettext(
                          'Employee can clock in and out within [[distance]] [[uints]] from the location’s address.'
                        )}
                      </TranslateWithComponents>
                    </Text14>
                  </Column>
                  <Row>
                    <Padding right={10}>
                      <StyledTextField
                        ref={radiusField}
                        value={userOrgSettings.clockInMaxDistance || ''}
                        onChange={(event): void => {
                          onUserOrgSettingsChange({
                            clockInMaxDistance: Number(event.currentTarget.value),
                          });
                          toggleError(
                            TabType.timeClock,
                            'clockInMaxDistance',
                            !validateMaxDistance(Number(event.currentTarget.value))
                          );
                        }}
                        error={getErrorMessage(userOrgSettings.clockInMaxDistance)}
                        invalid={!isFieldValid('clockInMaxDistance')}
                        onBlur={(): void =>
                          toggleError(
                            TabType.timeClock,
                            'clockInMaxDistance',
                            !validateMaxDistance(userOrgSettings.clockInMaxDistance)
                          )
                        }
                      />
                    </Padding>
                    <View>
                      <Picker
                        customWidth={FIELD_WIDTH_100}
                        options={UNIT_SYSTEMS}
                        value={getNumberFromBoolean(userOrgSettings.imperialUnits)}
                        onChange={(value: number): void =>
                          onUserOrgSettingsChange({ imperialUnits: Boolean(value) })
                        }
                      />
                    </View>
                  </Row>
                </StyledRow>

                <SettingsSwitcherRow
                  disabled={!currentUser.isAdmin}
                  pTop={20}
                  value={userOrgSettings.clockInAllowInvalidGeo}
                  onChange={(value): void =>
                    onUserOrgSettingsChange({ clockInAllowInvalidGeo: value })
                  }
                  subtitle={gettext(
                    'Allow clocking in and out outside of the radius. Employees’ distance will be recorded on the timesheets report.'
                  )}
                />
              </Column>
            }
          />
          <SettingsSwitcherRow
            disabled={!currentUser.isAdmin}
            pBottom={20}
            value={userOrgSettings.clockInIpCheckEnabled}
            onChange={(value: boolean): void =>
              onUserOrgSettingsChange({ clockInIpCheckEnabled: value })
            }
            title={gettext('Connected to location Wi-Fi (IP address)')}
          />
        </>
      }
    />
  );
};

export default Geofencing;
